import React, { FC, memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import DateRangePicker, { DateRange } from '@mui/lab/DateRangePicker';
import MobileDateRangePicker from '@mui/lab/MobileDateRangePicker';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { DateTime } from 'luxon';
import { Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AdapterDateLuxon } from '../../../utils';

interface FormDateRangePickerProps {
  minDate: DateTime | null;
  handleChange: (dates: DateRange<DateTime>) => void;
}

const FormDateRangePicker: FC<FormDateRangePickerProps> = ({ minDate, handleChange }) => {
  const { t, i18n } = useTranslation();
  const { control } = useFormContext();
  const locale = i18n.language === 'ua' ? 'uk' : i18n.language;
  const weekStartsOn = i18n.language === 'en' ? 7 : 1;
  const validationRules = {
    required: {
      value: true,
      message: t('validation.thisFieldIsRequired'),
    },
  };

  const handlePreventPaste = (e) => {
    e.preventDefault();
  };

  return (
    <Controller
      name="activationPeriod"
      control={control}
      rules={validationRules}
      render={({ field: { onChange, value } }) => {
        return (
          <>
            <div className="payment__content__options__activation-period__title">
              <article className="payment__content__label">{t('servicePayment.choosePeriodDaily')}</article>
            </div>
            <LocalizationProvider dateAdapter={AdapterDateLuxon(weekStartsOn)} locale={locale}>
              <div className="custom-date-range-picker_mobile">
                <MobileDateRangePicker
                  inputFormat="dd.MM.yyyy"
                  toolbarTitle={t('servicePayment.selectDateRange')}
                  // disablePast
                  startText={t('input.from')}
                  endText={t('input.to')}
                  value={value}
                  minDate={minDate}
                  okText=""
                  cancelText={t('buttons.close')}
                  className="custom-date-range-picker"
                  DialogProps={{ classes: { paper: 'custom-date-range-paper' } }}
                  onChange={(newValue) => {
                    const fromDate = newValue[0];
                    const toDate = newValue[1];
                    if (toDate === null) {
                      onChange([fromDate, fromDate]);
                      handleChange([fromDate, fromDate]);
                      return;
                    }
                    onChange(newValue);
                    handleChange(newValue);
                  }}
                  renderInput={(startProps, endProps) => {
                    return (
                      <>
                        <TextField
                          className="input input__text-field__primary"
                          onKeyDown={handlePreventPaste}
                          onPaste={handlePreventPaste}
                          {...startProps}
                        />
                        <Box sx={{ mx: 2 }} />
                        <TextField
                          className="input input__text-field__primary"
                          onKeyDown={handlePreventPaste}
                          onPaste={handlePreventPaste}
                          {...endProps}
                        />
                      </>
                    );
                  }}
                />
              </div>
              <div className="custom-date-range-picker_desktop">
                <DateRangePicker
                  inputFormat="dd.MM.yyyy"
                  // disablePast
                  startText={t('input.from')}
                  endText={t('input.to')}
                  value={value}
                  minDate={minDate}
                  okText=""
                  cancelText={t('buttons.close')}
                  className="custom-date-range-picker"
                  DialogProps={{ classes: { paper: 'custom-date-range-paper' } }}
                  onChange={(newValue) => {
                    const fromDate = newValue[0];
                    const toDate = newValue[1];
                    if (toDate === null) {
                      onChange([fromDate, fromDate]);
                      handleChange([fromDate, fromDate]);
                      return;
                    }
                    onChange(newValue);
                    handleChange(newValue);
                  }}
                  renderInput={(startProps, endProps) => (
                    <>
                      <TextField
                        className="input input__text-field__primary"
                        onKeyDown={handlePreventPaste}
                        onPaste={handlePreventPaste}
                        {...startProps}
                      />
                      <Box sx={{ mx: 2 }} />
                      <TextField
                        className="input input__text-field__primary"
                        onKeyDown={handlePreventPaste}
                        onPaste={handlePreventPaste}
                        {...endProps}
                      />
                    </>
                  )}
                />
              </div>
            </LocalizationProvider>
          </>
        );
      }}
    />
  );
};

export default memo(FormDateRangePicker);
